import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  ThemeProvider,
  createTheme,
  Autocomplete,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { Delete, Edit, Info as InfoIcon } from "@mui/icons-material";
import apiClient from "@lift-admin/utils/apiClient";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Toastify } from 'lift-ui';
import BrandMultiSelect from "./brand-multiselect";
import { CellStyle } from "ag-grid-community";
import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import { buildAccessListChange } from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";

const theme = createTheme({
  palette: {
    primary: {
      main: "#16306D",
    },
    secondary: {
      main: "rgba(0, 0, 0, 0)",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ItemsProps {
  searchText: string;
  toggle: string;
  channels: any[];
  openDialog: boolean;
  setOpenDialog: any;
  brands: any[];
}

interface FormStateEdit {
  name: string;
  icon: string;
  path: string;
  channel_id: number | null;
  brands_exclude: number[];
}

// Custom cell renderer for brand display
const BrandCellRenderer = (props: any) => {
  // Extract data from props
  const { value, data } = props;

  if (
    !data ||
    !data.brands_exclude ||
    !Array.isArray(data.brands_exclude) ||
    data.brands_exclude.length === 0
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>--</span>
      </div>
    );
  }

  // Split brands into excluded (negative IDs) and included (positive IDs)
  const brandIds = data.brands_exclude;
  const includedBrands = brandIds.filter((id: number) => id > 0);
  const excludedBrands = brandIds
    .filter((id: number) => id < 0)
    .map((id: number) => -id); // Convert to positive for lookup

  // Function to get brand names
  const getBrandNames = (ids: number[], brands: any[]) => {
    return ids
      .map((id: number) => {
        const brand = brands.find((b) => b.id === id);
        return brand ? brand.name : null;
      })
      .filter(Boolean);
  };

  // Get brand names
  const excludedBrandNames = getBrandNames(
    excludedBrands,
    props.context.brandsList
  );
  const includedBrandNames = getBrandNames(
    includedBrands,
    props.context.brandsList
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        maxHeight: "60px",
        overflow: "auto",
        scrollbarWidth: "thin",
      }}
    >
      {(excludedBrandNames.length > 0 || includedBrandNames.length > 0) && (
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            flexWrap: "wrap",
            paddingRight: "4px",
          }}
        >
          {/* Excluded brands (red) */}
          {excludedBrandNames.map((name: string, index: number) => (
            <Chip
              key={`excluded-${index}`}
              label={name}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "#d32f2f",
                "& .MuiChip-label": { fontSize: "0.75rem" },
                margin: "2px",
              }}
            />
          ))}

          {/* Included brands (green) */}
          {includedBrandNames.map((name: string, index: number) => (
            <Chip
              key={`included-${index}`}
              label={name}
              size="small"
              sx={{
                color: "#fff",
                bgcolor: "#2e7d32",
                "& .MuiChip-label": { fontSize: "0.75rem" },
                margin: "2px",
              }}
            />
          ))}
        </Box>
      )}

      {excludedBrandNames.length === 0 && includedBrandNames.length === 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>--</span>
        </div>
      )}
    </div>
  );
};

// Custom header component for Excluded/Include Brands
const CustomHeaderExcludedInclude = () => {
  return (
    <div>
      <span style={{ color: "red" }}>Excluded</span>/
      <span style={{ color: "green" }}>Include</span> Brands
    </div>
  );
};

const Groupings = ({
  toggle,
  channels,
  searchText,
  openDialog,
  setOpenDialog,
  brands,
}: ItemsProps) => {
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerGroupings, setDrawerGroupings] = useState<any>([]);
  const [drawerGroupingsBackUp, setDrawerGroupingBackup] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [currentIdData, setCurrentIdData] = useState<any>(null);
  const [name, setName] = useState<string | null>(null);
  const [icon, setIcon] = useState<string | null>(null);
  const [path, setPath] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [iconError, setIconError] = useState("");
  const [pathError, setPathError] = useState("");
  const [searchedResults, setSearchedResults] = useState<any>([]);
  const [formStateEdit, setFormStateEdit] = useState<FormStateEdit>({
    name: "",
    icon: "",
    path: "",
    channel_id: null,
    brands_exclude: [],
  });

  const getDrawrGroupings = async () => {
    setLoadData(true);
    try {
      const res = await apiClient.get(
        "/drawer/v1/admin/drawer/structure/grouping"
      );

      const grouping = res?.data?.response?.grouping.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );

      if (searchText.trim().length === 0) {
        setDrawerGroupings(grouping);
      } else {
        setDrawerGroupings(
          grouping.filter((item: any) => searchedResults.includes(item.id))
        );
      }

      setDrawerGroupingBackup(grouping);
    } catch {
      Toastify("error", "Verticals not fetched");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    getDrawrGroupings();
  }, []);

  const channelMapping = channels?.reduce((acc, grouping) => {
    acc[grouping.id] = grouping.name;
    return acc;
  }, {});

  const rowData = useMemo(() => drawerGroupings, [drawerGroupings]);

  // Define cell styles as CellStyle objects
  const centerCellStyle: CellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const startCellStyle: CellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  };

  const brandsCellStyle: CellStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    paddingTop: "8px",
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        width: 300,
        sortable: true,
        resizable: true,
        editable: true,
        flex: 1,
        cellStyle: startCellStyle,
      },

      {
        field: "path",
        headerName: "Path",
        width: 200,
        sortable: true,
        resizable: true,
        editable: true,
        flex: 1,
        cellStyle: startCellStyle,
      },
      {
        field: "brands_exclude",
        headerName: "Excluded/Include Brands",
        width: 300,
        sortable: true,
        resizable: true,
        editable: false,
        cellRenderer: BrandCellRenderer,
        headerComponent: CustomHeaderExcludedInclude,
        flex: 1.5,
        cellStyle: brandsCellStyle,
      },
      {
        field: "channel_id",
        headerName: "Channel",
        width: 300,
        sortable: true,
        resizable: true,
        editable: true,
        flex: 1,
        valueGetter: (params: any) =>
          channelMapping[params.data.channel_id] || "",
        cellStyle: startCellStyle,
      },
      {
        field: "created_at",
        headerName: "Created At",
        width: 200,
        sortable: true,
        resizable: true,
        editable: false,
        flex: 1,
        cellStyle: startCellStyle,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        },
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        width: 200,
        sortable: true,
        resizable: true,
        editable: false,
        flex: 1,
        cellStyle: startCellStyle,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        resizable: false,
        editable: false,
        flex: 1,
        headerClass: "center-header",
        cellStyle: centerCellStyle,
        pinned: "right" as const,
        cellRenderer: (params: { data: any }) => {
          return (
            <div style={{ display: "flex", gap: "5px" }}>
              <Button
                onClick={() => handleEditOpen(params?.data)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Edit style={{ color: "black" }} />
              </Button>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(true);
                  setCurrentIdData(params?.data);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Delete style={{ color: "red" }} />
              </Button>
            </div>
          );
        },
      },
    ],
    [channels]
  );

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
  };

  const handleAddChannel = async () => {
    const kebabCaseRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

    let valid = true;

    if (path && !kebabCaseRegex.test(path)) {
      setPathError("Path should be in kebab-case");
      valid = false;
    } else {
      setPathError("");
    }

    if (!name || name.length == 0) {
      Toastify("error", "Please enter correct name");
      return;
    }

    if (!valid) return;

    const payload = {
      name: name,
      icon: icon,
      path: path,
      channel_id: selectedChannel?.id,
    };
    try {
      const res = await apiClient.post(
        "/drawer/v1/admin/drawer/structure/grouping",
        {
          name: name,
          icon: icon,
          path: path,
          channel_id: selectedChannel?.id,
        }
      );
      const groupingFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
        { key: "path", label: "Path" },
        {
          key: "channel_id",
          label: "Channel",
          resolve: (id) =>
            channelMapping?.[id] || (id ? String(id) : "Not Set"),
        },
      ];
      const normalizedGrouping = {
        name: payload.name,
        icon: payload.icon,
        path: payload.path,
        channel_id: payload.channel_id,
      };
      const desc = {
        message: buildSummary("Grouping", payload?.name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_grouping",
        grouping_name: payload?.name,
        details: buildDetails(normalizedGrouping, groupingFieldSpecs, {
          skipEmpty: true,
        }),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getDrawrGroupings();
      setName("");
      setIcon("");
      setPath("");
      setOpenDialog(false);
    } catch (err: any) {
      setOpenDialog(false);
      Toastify("error", "Grouping not added");
      const errorDesc = {
        message: `Failed to create Grouping (Grouping Name: "${name}")`,
        status: err?.response?.status,
        method: "POST",
        url: "/drawer/v1/admin/drawer/structure/grouping",
        grouping_name: name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const handleDelete = async (data: any) => {
    try {
      const res = await apiClient.delete(
        `/drawer/v1/admin/drawer/structure/grouping/${data?.id}`
      );
      const desc = {
        message: buildSummary("Grouping", data?.name, "deleted"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "delete_grouping",
        grouping_id: data?.id,
        grouping_name: data?.name,
        details: [
          { label: "Grouping ID", value: String(data?.id ?? "Not Set") },
          {
            label: "Channel",
            value:
              channelMapping?.[data?.channel_id] ||
              String(data?.channel_id ?? "Not Set"),
          },
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getDrawrGroupings();
      setOpenDeleteDialog(false);
      Toastify("success", "Item Deleted successfully");
    } catch (err: any) {
      Toastify("error", "Grouping not deleted");
      const errorDesc = {
        message: `Failed to delete Grouping (Grouping ID: ${data?.id}, Grouping Name: "${data?.name}")`,
        status: err?.response?.status,
        method: "DELETE",
        url: `/drawer/v1/admin/drawer/structure/grouping/${data?.id}`,
        grouping_id: data?.id,
        grouping_name: data?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const handleEdit = async () => {
    if (!currentIdData?.id) return;

    try {
      const res = await apiClient.put(
        `/drawer/v1/admin/drawer/structure/grouping/${currentIdData?.id}`,
        formStateEdit
      );
      Toastify("success", "Item updated successfully");
      const nextBrandsExclude = [...(formStateEdit.brands_exclude || [])];
      const groupingFieldSpecs: FieldSpec[] = [
        { key: "name", label: "Name" },
        { key: "icon", label: "Icon" },
        { key: "path", label: "Path" },
        {
          key: "channel_id",
          label: "Channel",
          resolve: (id) =>
            channelMapping?.[id] || (id ? String(id) : "Not Set"),
        },
      ];
      const previousGrouping = {
        name: currentIdData?.name,
        icon: currentIdData?.icon,
        path: currentIdData?.path,
        channel_id: currentIdData?.channel_id,
      };
      const nextGrouping = {
        name: formStateEdit?.name,
        icon: formStateEdit?.icon,
        path: formStateEdit?.path,
        channel_id: formStateEdit?.channel_id,
      };
      const changes = diffFields(
        previousGrouping,
        nextGrouping,
        groupingFieldSpecs
      );
      const brandChange = buildAccessListChange(
        "Brand Access",
        currentIdData?.brands_exclude || [],
        nextBrandsExclude,
        brands || [],
        "Brand"
      );
      if (brandChange) changes.push(brandChange);
      const desc = {
        message: buildSummary(
          "Grouping",
          formStateEdit?.name || currentIdData?.name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_grouping",
        grouping_id: currentIdData?.id,
        grouping_name: formStateEdit?.name || currentIdData?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
      getDrawrGroupings();
      handleEditDialogClose();
    } catch (err: any) {
      Toastify("error", "Item not updated");
      const errorDesc = {
        message: `Failed to update Grouping (Grouping ID: ${currentIdData?.id}, Grouping Name: "${formStateEdit?.name || currentIdData?.name}")`,
        status: err?.response?.status,
        method: "PUT",
        url: `/drawer/v1/admin/drawer/structure/grouping/${currentIdData?.id}`,
        grouping_id: currentIdData?.id,
        grouping_name: formStateEdit?.name || currentIdData?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const handleEditOpen = (data: any) => {
    setCurrentIdData(data);
    setFormStateEdit({
      name: data?.name,
      icon: data?.icon,
      path: data?.path,
      channel_id: data?.channel_id,
      brands_exclude: data?.brands_exclude || [],
    });
    const selectedChannelObj = channels.find(
      (channel) => channel.id === data?.channel_id
    );
    setSelectedChannel(selectedChannelObj);
    setOpenEditDialog(true);
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditDialogClose = () => {
    setFormStateEdit({
      name: "",
      icon: "",
      path: "",
      channel_id: null,
      brands_exclude: [],
    });
    setCurrentIdData(null);
    setOpenEditDialog(false);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setName("");
    setIcon("");
  };

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerGroupingsBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (
        filteredUsers[i]["name"]?.toLowerCase().includes(query.toLowerCase()) ||
        filteredUsers[i]["path"]?.toLowerCase().includes(query.toLowerCase())
      )
        results.push(filteredUsers[i]);
    }

    setSearchedResults(results.map((item: any) => item.id));
    setDrawerGroupings(results);
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <Paper className="table-paper" style={{ paddingLeft: 0 }}>
          <div
            className="ag-theme-alpine"
            style={{ height: "100%", width: "100%" }}
          >
            <AgGridReact
              columnDefs={columnDefs.map((colDef) => ({
                ...colDef,
                editable: false,
              }))}
              rowData={rowData}
              defaultColDef={defaultColDef}
              rowHeight={90}
              pagination={true}
              paginationPageSize={20}
              suppressPaginationPanel={false}
              context={{ brandsList: brands }}
            />
          </div>
        </Paper>
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Are you sure you want to delete?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleDelete(currentIdData);
              }}
              color="primary"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>Add New Grouping</DialogTitle>
          <DialogContent style={{ padding: "10px" }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => {
                const newName = e.target.value;
                setName(newName);
                setPath(newName.toLowerCase().replace(/\s+/g, "-"));
              }}
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              disabled
              label="Path"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              fullWidth
              error={!!pathError}
              helperText={pathError}
              style={{ marginBottom: "16px" }}
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              options={channels}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => setSelectedChannel(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Channel Name"
                  fullWidth
                  style={{ marginBottom: "16px" }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openEditDialog}
          onClose={handleEditDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Grouping</DialogTitle>
          <DialogContent style={{ padding: "16px" }}>
            <TextField
              label="Name"
              value={formStateEdit.name}
              onChange={(e) => {
                const newName = e.target.value;
                setFormStateEdit((prev) => ({
                  ...prev,
                  name: newName,
                  path: newName.toLowerCase().replace(/\s+/g, "-"),
                }));
              }}
              fullWidth
              style={{ marginBottom: "16px" }}
            />
            <TextField
              disabled
              label="Path"
              value={formStateEdit.path}
              fullWidth
              error={!!pathError}
              helperText={pathError}
              style={{ marginBottom: "16px" }}
            />
            <Autocomplete
              options={channels}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => {
                setSelectedChannel(value);
                setFormStateEdit((prev) => ({
                  ...prev,
                  channel_id: value?.id || null,
                }));
              }}
              value={selectedChannel}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Channel Name"
                  fullWidth
                  style={{ marginBottom: "16px" }}
                />
              )}
            />

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Brand Configuration:
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <InfoIcon
                fontSize="small"
                style={{ verticalAlign: "middle", marginRight: "4px" }}
              />
              Use negative IDs (e.g., -1, -2) to exclude specific brands. Use
              positive IDs (e.g., 1, 2) to include only those specific brands.
            </Typography>

            <BrandMultiSelect
              defaultBrands={formStateEdit.brands_exclude}
              setDefaultBrands={(field: string, value: number[]) => {
                setFormStateEdit((prev) => ({
                  ...prev,
                  brands_exclude: value,
                }));
              }}
            />

            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography variant="subtitle2">
                Current Selection Preview:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formStateEdit.brands_exclude
                  .filter((id) => id < 0)
                  .map((id) => {
                    const brandName =
                      brands.find((b) => b.id === -id)?.name ||
                      `Unknown (ID: ${-id})`;
                    return (
                      <Chip
                        key={`preview-excluded-${id}`}
                        label={brandName}
                        size="small"
                        sx={{ bgcolor: "#d32f2f", color: "white" }}
                      />
                    );
                  })}

                {formStateEdit.brands_exclude
                  .filter((id) => id > 0)
                  .map((id) => {
                    const brandName =
                      brands.find((b) => b.id === id)?.name ||
                      `Unknown (ID: ${id})`;
                    return (
                      <Chip
                        key={`preview-included-${id}`}
                        label={brandName}
                        size="small"
                        sx={{ bgcolor: "#2e7d32", color: "white" }}
                      />
                    );
                  })}

                {formStateEdit.brands_exclude.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    All brands will be included (no restrictions)
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleEdit} color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default Groupings;
